/** /agroup_template_groups/, /agroup_templates/ + RBAC lookups.
 *
 * Access group templates: system-wide templates (tour_operator = null) are
 * materialised per tenant into tenant-scoped copies whose managed access
 * groups they keep in sync. See the backend engine
 * (demo/lib/agroups/templates.py) for semantics.
 */
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { PaginatedResponse } from './users';

export interface AgroupTemplateGroup {
  id: number;
  name: string;
  description: string;
  template_count: number;
  materialised_tenant_count: number;
  created_ts: string | null;
  updated_ts: string | null;
}

export interface EmployeePermission {
  id: number;
  codename: string;
  name: string;
  has_target_tags: boolean;
  has_target_agroups: boolean;
}

export interface AgroupTemplateArgument {
  id: number;
  position: number;
  alias: string;
  referenced_by: string[];
}

export interface AgroupScopeTemplate {
  id: number;
  perm: EmployeePermission;
  argument_aliases: string[];
  all_tags: boolean;
  target_template_ids: number[];
  target_guest_group: boolean;
}

export interface TemplateWarning {
  code: string;
  message: string;
  template_id: number | null;
  agroup_id: number | null;
}

export const ROLE_NONE = 0;
export const ROLE_ADMIN = 1;
export const ROLE_TOUR_GUIDE = 2;
export type RoleFlag = 0 | 1 | 2;
export const ROLE_LABELS: Record<RoleFlag, string> = {
  [ROLE_NONE]: 'None',
  [ROLE_ADMIN]: 'Admin',
  [ROLE_TOUR_GUIDE]: 'Tour guide',
};

export interface AgroupTemplate {
  id: number;
  tour_operator: { id: number; code: string; name: string } | null;
  group: number;
  group_name: string;
  name: string;
  codename: string;
  parent: number | null;
  source: number | null;
  role: RoleFlag;
  propagated_role: RoleFlag;
  arguments: AgroupTemplateArgument[];
  scope_templates: AgroupScopeTemplate[];
  managed_group_count: number;
  created_ts: string | null;
  updated_ts: string | null;
  propagation?: PropagationResult;
}

export interface ScopeTemplateWriteInput {
  id?: number;
  perm: number | string;
  argument_aliases: string[];
  all_tags: boolean;
  target_template_ids: number[];
  target_guest_group: boolean;
}

export interface AgroupTemplateWriteInput {
  group: number;
  tour_operator?: number | null;
  name: string;
  codename: string;
  parent?: number | null;
  role: RoleFlag;
  propagated_role: RoleFlag;
  arguments?: { id?: number; position: number; alias: string }[];
  scope_templates?: ScopeTemplateWriteInput[];
}

export interface MaterialisationResult {
  tenant_templates: Record<string, number>;
  created_group_ids: number[];
  updated_group_ids: number[];
  skipped_group_ids: number[];
  warnings: TemplateWarning[];
}

export interface PropagationResult {
  updated_group_ids: number[];
  skipped_group_ids: number[];
  warnings: TemplateWarning[];
}

export interface AccessTag {
  id: number;
  name: string;
}

export interface EmployeeRole {
  id: number;
  name: string;
  is_admin_guess: boolean;
  is_guide_guess: boolean;
}

export type TemplateScope = 'system' | number;

export const rbacApi = {
  // ---- template groups -----------------------------------------------------
  listGroups(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    ordering?: string;
    name_contains?: string;
    description_contains?: string;
  }) {
    return apiGet<PaginatedResponse<AgroupTemplateGroup>>('/agroup_template_groups/', {
      params: {
        page: params.page,
        page_size: params.pageSize,
        search: params.search || undefined,
        ordering: params.ordering,
        name_contains: params.name_contains || undefined,
        description_contains: params.description_contains || undefined,
      },
    });
  },
  createGroup(body: { name: string; description?: string }) {
    return apiPost<AgroupTemplateGroup>('/agroup_template_groups/', body);
  },
  updateGroup(id: number, body: { name?: string; description?: string }) {
    return apiPatch<AgroupTemplateGroup>(`/agroup_template_groups/${id}/`, body);
  },
  deleteGroup(id: number) {
    return apiDelete<void>(`/agroup_template_groups/${id}/`);
  },
  materialise(id: number, body: { tour_operator: number; values: Record<string, (number | string)[]> }) {
    return apiPost<MaterialisationResult>(`/agroup_template_groups/${id}/materialise/`, body);
  },

  // ---- templates -------------------------------------------------------------
  listTemplates(params: { scope: TemplateScope; group?: number; search?: string }) {
    return apiGet<PaginatedResponse<AgroupTemplate>>('/agroup_templates/', {
      params: {
        tour_operator: String(params.scope),
        group: params.group,
        search: params.search || undefined,
        page_size: 500, // full set; the grid renders a client-side tree
      },
    });
  },
  getTemplate(id: number) {
    return apiGet<AgroupTemplate>(`/agroup_templates/${id}/`);
  },
  createTemplate(body: AgroupTemplateWriteInput) {
    return apiPost<AgroupTemplate>('/agroup_templates/', body);
  },
  updateTemplate(id: number, body: Partial<AgroupTemplateWriteInput>) {
    return apiPatch<AgroupTemplate>(`/agroup_templates/${id}/`, body);
  },
  deleteTemplate(id: number) {
    return apiDelete<void>(`/agroup_templates/${id}/`);
  },
  propagate(id: number) {
    return apiPost<PropagationResult>(`/agroup_templates/${id}/propagate/`);
  },

  // ---- lookups ----------------------------------------------------------------
  listEmployeePermissions(search?: string) {
    return apiGet<{ results: EmployeePermission[] }>('/employee_permissions/', {
      params: { search: search || undefined },
    });
  },
  listAccessTags(topId: number, search?: string) {
    return apiGet<{ results: AccessTag[] }>(`/tour_operators/${topId}/access_tags/`, {
      params: { search: search || undefined },
    });
  },
  createAccessTag(topId: number, name: string) {
    return apiPost<AccessTag>(`/tour_operators/${topId}/access_tags/`, { name });
  },
  listEmployeeRoles(topId: number) {
    return apiGet<{ results: EmployeeRole[] }>(`/tour_operators/${topId}/employee_roles/`);
  },
};
